import React from 'react';
import Link from 'next/link';
import { Typography } from '@/components/common';
import AppRoutes from '@/const/routes';

interface Props {
	name: string;
	fullName: string;
}

export const RetainerMassActionBody: React.FC<Props> = ({ name, fullName }) => {
	return (
		<div className="space-y-6 text-[#0D0616] opacity-75 mb-4">
			<Typography type="regular-14" className="break-words">
				Dear {`${name}`}:
			</Typography>

			<Typography type="semibold-18" className="underline">
				I. Engagement and Scope of Representation
			</Typography>

			<Typography type="regular-14" className="text-justify break-words">
				This Retainer Agreement (&rdquo;Agreement&rdquo;) is entered into
				between the law firm Lowey Dannenberg, P.C. (&rdquo;Lowey&rdquo; or the
				&rdquo;Firm&rdquo;) and {`${fullName}`} (&rdquo;You&rdquo; or
				&rdquo;Your&rdquo;) whereby You retain and employ the Firm to represent
				You individually in connection with potential statutory and common law
				privacy claims.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				(&rdquo;Respondent&rdquo;) for their alleged improper collection and
				sharing of your personal information.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				The Firm will represent You as a claimant in connection with mass
				arbitration proceedings and/or related legal actions (collectively, the
				&rdquo;Action&rdquo;) against Respondent. The scope of this
				representation is limited to the claims described herein against
				Respondent and does not extend to any other matters unless separately
				agreed to in writing.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You are a separate and individual client of the Firm. You are not a
				member of a group, class, or pool that is collectively represented. Your
				legal interests are independent, and your decision-making authority is
				your own. The Firm&apos;s representation of other individuals with
				similar claims does not diminish Your individual rights as a client.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You have the right to consult with another attorney before signing this
				Agreement.
			</Typography>

			<Typography type="semibold-18" className="underline">
				II. Contingency Fee Arrangement
			</Typography>

			<Typography type="regular-14" className="text-justify">
				The Firm will represent You on a contingency fee basis. This means You
				will not be personally responsible for the payment of any legal fees to
				Lowey regardless of the outcome of the Action. Legal fees are the fees
				associated with an attorney&apos;s time spent on your case. Lowey will
				only receive compensation if it is successful in recovering money for
				You, for example as part of a settlement, at trial, or award following
				an arbitration. In the context of arbitration proceedings, the
				Firm&apos;s contingency fee is earned upon the issuance of a final award
				or the execution of a settlement agreement, whichever occurs first.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				In the event that Lowey is successful in recovering any money as part of
				a settlement, arbitration award, judgment, verdict, or otherwise, Lowey
				shall be entitled to forty percent (40%) of the total amount recovered
				in attorneys&apos; fees and reimbursement of its expenses. These
				attorneys&apos; fees and expenses may be paid from the settlement,
				judgment, or other award, or separately by one or more of the
				Respondents in the Action and will not be charged to You directly. The
				Firm&apos;s contingency fee will be calculated on the total gross
				recovery before the deduction of litigation expenses and costs.
				Litigation expenses and costs advanced by the Firm will be deducted from
				the remaining balance after the contingency fee is calculated.
				Alternatively, in the event a Respondent agrees to pay attorneys&apos;
				fees and expenses separate from any recovery paid to You (e.g., as part
				of an agreement to pay attorney&apos;s fees pursuant to an arbitration
				agreement or as part of settlement terms), then Lowey will be entitled
				to the maximum amount of attorney&apos;s fees and expenses available
				under that arbitration agreement or settlement, even if it is more than
				40% of your individual recovery. The contingency fee rate set forth in
				this Agreement is not established by law and is negotiable between you
				and the Firm. Arbitration awards may be paid directly to the Firm on
				Your behalf, and the Firm will disburse Your share of the award net of
				fees and expenses within a reasonable time following receipt of payment.
				In the event that some claimants represented by the Firm settle their
				claims and others do not, then attorneys’ fees and expenses will be
				apportioned based on the recovery attributable to each individual
				claimant’s settlement or award. Claimants who do not settle will not be
				responsible for attorneys’ fees and expenses attributable to the
				settlement of other claimants’ claims, however, they will also not
				receive any proceeds from those settlements. Lowey will advance all
				Action expenses on Your behalf. The Firm anticipates such expenses may
				include filing fees, arbitration administrative fees, arbitrator or
				mediator fees, court reporting and transcript costs, expert fees,
				research fees, document copying, reasonable travel costs, overnight
				delivery charges and postage, and other office expenses related to Your
				case. Please note that arbitration proceedings may involve substantial
				per-claimant institutional filing fees imposed by the arbitral forum
				(e.g., the AAA, NAM, or JAMS), which Lowey will advance on Your behalf
				and seek to recover from any settlement or award. We make no warranties
				or representations concerning the eventual outcome of the Action.
				Arbitration proceedings involve inherent uncertainty and risk, and while
				we will pursue Your claims diligently, we cannot guarantee any
				particular result.
			</Typography>

			<Typography type="semibold-18" className="underline">
				III. The Arbitration Process – Important Disclosures
			</Typography>

			<Typography type="regular-14" className="text-justify">
				If informal resolution or settlement discussions are unsuccessful, the
				Firm may initiate arbitration proceedings on Your behalf before the
				applicable arbitral forum designated in Your agreement with Respondent.
				You understand that arbitration is a binding legal proceeding that may
				result in a final award in Your favor or against You. The Firm will
				notify You of any scheduled arbitration proceedings and will keep You
				reasonably informed of developments. Any monetary award issued in Your
				favor will be subject to the same contingency fee arrangement described
				in Section II of this Agreement.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You understand and acknowledge that your arbitration claims, while filed
				separately, may proceed in tandem with several other individuals as part
				of a mass arbitration. Please read this section carefully.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">The Nature of Mass Arbitration.</span>{' '}
				Mass arbitration involves a large number of individuals filing similar
				claims against the same respondent, coordinated for purposes of
				efficiency. Each claim is filed as a separate case, and each
				claimant&apos;s claims are resolved on their own merits, even if
				procedurally coordinated with others.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">Arbitration vs. Court.</span>{' '}
				Arbitration is required through Your agreement with Respondent. By
				agreeing to pursue Your claims in arbitration, You waive certain rights
				You would have in a court proceeding, including:
			</Typography>

			<ul className="list-disc pl-8 space-y-2">
				<li>
					<Typography type="regular-14" className="text-justify">
						The right to a trial before a judge or jury in a public courtroom.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						The right to appeal an award or verdict in most circumstances.
						Arbitration awards are generally final and binding.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Access to the full range of discovery available in court
						proceedings. Discovery in arbitration is typically more limited.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Public access to the proceedings. Arbitration is a confidential
						process, and awards are not publicly accessible.
					</Typography>
				</li>
			</ul>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">Batching and Coordination.</span> Your
				claims may be filed, grouped, and resolved in batches with other
				similarly situated claimants for administrative efficiency. The timing
				of the resolution of Your claims may depend on the batch in which Your
				claims are filed in and/or administrated by the arbitration forum.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">Pre-Arbitration Requirements.</span>{' '}
				Prior to initiating arbitration, an arbitration agreement with
				Respondent may require You to participate in an informal dispute
				resolution process, which may include signing and sending a notice of
				dispute letter and/or participating in a conference. The Firm will guide
				You through these steps on Claim Magic.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You represent and warrant that: (a) You have entered into an agreement
				with Respondent that contains an arbitration clause; (b) You have not
				opted out of the arbitration clause in Respondent&apos;s agreement; and
				(c) You are not aware of any reason that would prevent You from
				participating in arbitration against Respondent.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">Post-Arbitration Process.</span>{' '}
				Following the issuance of an arbitration award in Your favor, the Firm
				will take all reasonable steps to enforce and collect the award on Your
				behalf, including filing a motion to confirm the award in a court of
				competent jurisdiction if necessary. Costs associated with enforcement
				proceedings will be treated as litigation expenses advanced by the Firm
				and subject to the fee arrangement described in Section II.
			</Typography>

			<Typography type="semibold-18" className="underline">
				IV. Settlement – Your Rights Under the Aggregate Settlement Rule
			</Typography>

			<Typography type="regular-14" className="text-justify">
				This section contains important information about your legal rights with
				respect to any settlement of your claims. Please read it carefully.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">Individual Settlement Rights.</span> The
				decision whether to accept a settlement offer belongs to You
				individually. The Firm will not settle Your individual claims without
				Your informed consent. You have the absolute right to accept or reject
				any settlement offer made with respect to Your claims. Given the timing
				consideration of settlement offers, You will need to frequently check
				Your Claim Magic account to see if we have communicated any settlement
				offers to You. This is extremely important as settlement offers often
				expire after a set time (e.g., 30 days) such that, if you do not respond
				within that period, you may lose the right to participate entirely.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				<span className="font-semibold">Aggregate Settlements.</span> Because
				the Firm represents a large number of claimants with similar claims, any
				settlement may be negotiated on an aggregate basis — meaning a single
				settlement will cover the collective claims of multiple claimants. In
				the event of an aggregate settlement:
			</Typography>

			<ul className="list-disc pl-8 space-y-2">
				<li>
					<Typography type="regular-14" className="text-justify">
						Your individual approval is still required. Even if claims settle in
						the aggregate, You will not be included in that settlement without
						Your individual informed written consent. Your consent cannot be
						waived in advance.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Before You are asked to consent to any aggregate settlement, the
						Firm will disclose to You: (a) the total amount of the settlement;
						(b) a description of all claims covered; (c) Your individual share
						of the settlement proceeds; (d) total fees and costs to be paid from
						the settlement; (e) the method for apportioning fees and costs among
						claimants; and (f) when you will have to respond by.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						You will have a reasonable opportunity to evaluate the proposed
						settlement terms and consult with the Firm before making your
						decision.
					</Typography>
				</li>
			</ul>

			<Typography type="regular-14" className="text-justify">
				The Firm is dedicated to obtaining your consent and will use reasonable
				and efficient means — including the Claim Magic platform — to keep you
				informed and facilitate this process.
			</Typography>

			<Typography type="semibold-18" className="underline">
				V. Conflicts of Interest Disclosure and Informed Consent
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You acknowledge and understand that the Firm represents, and may
				represent, a large number of other individuals with substantially
				similar claims against Respondent (&rdquo;Other Claimants&rdquo;). While
				the Firm believes that representing multiple claimants with similar
				claims does not create a disqualifying conflict of interest, You should
				be aware of the following:
			</Typography>

			<ul className="list-disc pl-8 space-y-2">
				<li>
					<Typography type="regular-14" className="text-justify">
						Your claims may differ from the claims of other individual claimants
						in strength, damages, or other material respects.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						In negotiating a settlement, the Firm will seek to maximize each
						individual claimant&apos;s recovery as much as possible. However,
						this may result in different claimants or groups of claimants
						receiving different amounts of money from You based on the strengths
						and weaknesses of their respective claims.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						The Firm&apos;s duty of loyalty runs to You individually. However,
						practical decisions about litigation strategy may be made globally,
						on behalf of all claimants, where appropriate and consistent with
						the Firm&apos;s professional obligations.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Should a material conflict of interest arise between Your interests
						and those of Other Claimants, the Firm will advise You promptly and
						take appropriate action. This may include withdrawal of
						representation for one or more claimants.
					</Typography>
				</li>
			</ul>

			<Typography type="regular-14" className="text-justify">
				By signing this Agreement, You provide informed written consent to the
				Firm&apos;s joint representation of You and Other Claimants, with full
				awareness of the foregoing considerations.
			</Typography>

			<Typography type="semibold-18" className="underline">
				VI. Claim Magic Platform and Electronic Communications
			</Typography>

			<Typography type="regular-14" className="text-justify">
				The Firm uses Claim Magic, a centralized case management platform, to
				manage Your case, collect documents, and communicate developments in the
				Action to You. By signing this Agreement, You authorize the Firm to
				collect, store, and manage Your personal information and documents
				through Claim Magic for the purpose of evaluating and pursuing Your
				legal claims.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You consent to receiving communications from the Firm electronically,
				including through the Claim Magic platform, email, and/or text message.
				Please review the Claim Magic Privacy Policy (available{' '}
				<Link
					href={AppRoutes.LANDING_PRIVACY_POLICY}
					target="_blank"
					className="underline text-[#236A96]"
				>
					Here
				</Link>
				) and the Claim Magic Terms of Use (available{' '}
				<Link
					href={AppRoutes.LANDING_TERMS_OF_USE}
					target="_blank"
					className="underline text-[#236A96]"
				>
					Here
				</Link>
				), which outline how your personal information is used to advance your
				claims. All electronic signatures provided through Claim Magic or
				otherwise are valid and binding.
			</Typography>

			<Typography type="semibold-18" className="underline">
				VII. Association with Other Firms and Fee Sharing
			</Typography>

			<Typography type="regular-14" className="text-justify">
				In order to pursue Your claims, the Firm may associate with one or more
				other law firms (&rdquo;Co-Counsel&rdquo;). If Co-Counsel is engaged,
				You will be notified of their identity, their role in the
				representation, and the fee-sharing arrangement between the Firm and
				Co-Counsel. Any Co-Counsel that agrees to be named as counsel for You
				will assume joint responsibility for Your representation, will be bound
				by the terms and conditions as set forth herein, and may receive
				communications about Your claims. Any attorneys&apos; fees shared with
				Co-Counsel will be apportioned from Firm&apos;s fee and will not
				increase the total fee charged to You.
			</Typography>

			<Typography type="semibold-18" className="underline">
				VIII. Your Responsibilities
			</Typography>

			<Typography type="regular-14" className="text-justify">
				Throughout the Action You will be responsible for the following, but not
				limited to:
			</Typography>

			<ul className="list-disc pl-8 space-y-2">
				<li>
					<Typography type="regular-14" className="text-justify">
						Monitoring case progress through Claim Magic and other
						communications from the Firm.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Responding promptly to the Firm&apos;s requests for documents or
						information.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Being reasonably available for deposition, arbitration, or other
						proceedings, if necessary.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Immediately preserving and refraining from deleting or destroying
						all documents, data, and devices (e.g., Your cell phone or
						computers) that may be related to the Action, including anything
						from at least June 2020 to date.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Refraining from accepting, agreeing to, or signing any updated terms
						of service with Respondent, as doing so may directly impact the
						claims the Firm can assert on Your behalf, without first notifying
						the Firm of any changes Respondent has sent.
					</Typography>
				</li>
				<li>
					<Typography type="regular-14" className="text-justify">
						Keeping Your contact information current on Claim Magic, including
						Your name, address, phone number, and email address.
					</Typography>
				</li>
			</ul>

			<Typography type="semibold-18" className="underline">
				IX. Communication and Case Updates
			</Typography>

			<Typography type="regular-14" className="text-justify">
				The Firm shall keep You advised of all material events in this Action,
				as is The Firm&apos;s responsibility as attorneys. The Firm shall
				provide You with copies of all significant pleadings, motions, or
				settlement demands in the Action and may ask You to review and approve
				certain items before the Firm can take action on Your behalf. The Firm
				requires reasonable cooperation from You throughout the case, such as
				responding to discovery or other requests for information from
				Respondent or offers to resolve Your claims. The Firm will use the Claim
				Magic platform as its primary means of communicating case updates to You
				and it is Your obligation to check it regularly.
			</Typography>

			<Typography type="semibold-18" className="underline">
				X. Governing Law, Termination, and Withdrawal
			</Typography>

			<Typography type="regular-14" className="text-justify">
				You shall have the right at any time to terminate the Firm&apos;s
				services and representation upon written notice. This right is absolute
				and may not be conditioned or penalized. If You terminate, You will not
				owe Lowey for attorneys&apos; fees and expenses. Lowey will be entitled
				to seek reimbursement for attorneys&apos; fees and expenses incurred
				prior to termination from any eventual settlement, verdict, award, or
				other resolution reached in the Action. Unless specifically agreed by
				You and Lowey in writing, Lowey will provide no further services and
				advance no further costs on Your behalf after receipt of the termination
				notice.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				This Agreement shall be governed by and construed in accordance with the
				laws of the State of New York, without regard to its conflict of laws
				principles. If the governing law clause is not enforceable in a
				particular jurisdiction, the law of that jurisdiction will apply to the
				extent required.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				In certain circumstances, You may have the right to arbitration of fee
				disputes under 22 N.Y.C.R.R. § 137. California residents may also
				arbitrate fee disputes through the California State Bar.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				The Firm also reserves the right to terminate its representation of You
				at any time, for any reason, including if You fail to timely respond to
				the Firm&apos;s communications on Claim Magic, meaningfully participate
				in the Action, or if there is a disagreement regarding case prosecution
				of Your case. The Firm will provide You with reasonable notice before
				withdrawal.
			</Typography>

			<Typography type="semibold-18" className="underline">
				XI. Entire Agreement
			</Typography>

			<Typography type="regular-14" className="text-justify">
				This Agreement constitutes the entire agreement between You and the Firm
				with respect to the subject matter hereof and supersedes all prior
				agreements and understandings, whether written or oral.
			</Typography>

			<Typography type="semibold-18" className="underline">
				XII. Acknowledgment
			</Typography>

			<Typography type="regular-14" className="text-justify">
				By signing below, You acknowledge that: (a) You have read and understood
				this Agreement in its entirety; (b) You have had the opportunity to ask
				questions and consult with independent counsel before signing (if you so
				choose); (c) You understand the nature of mass arbitration and the
				rights You are and are not waiving; (d) You consent to the Firm&apos;s
				joint representation of You and Other Claimants as described herein; and
				(e) You agree to be bound by the terms of this Agreement.
			</Typography>

			<Typography type="regular-14" className="text-justify">
				We make no warranties or representations concerning the eventual outcome
				of any action in which you serve as a claimant. If you find everything
				in order, please sign below. We greatly appreciate the opportunity to be
				of service to You and look forward to working on Your behalf.
			</Typography>
		</div>
	);
};
